import usePlot from "./usePlot";
import useDroughtEvent from "./useDroughtEvents";
import useIPCScore from "./useIPCScore";
import useQueryState from "./useQueryState";
import useClimateTimeseries from "./useTimeseriesClimate";
import useTimeseriesAnomaly from "./useTimeseriesAnomaly";
import { useMemo } from "react";
import useHybridIndexTimeseries from "./useTimeseriesHybridIndex";
import useCountries from "./useCountries";
import { useUIStore } from "../store/ui";
import { buildIndicatorName } from "../helpers/indicatorName";

const useTimeseriesSimple = ({
  type,
  season,
}: {
  type: string;
  season?: string;
}) => {
  const isDetrended = useUIStore((s) => s.isDetrended);
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);
  const {
    queryState: { droughtEvents, ipcScore, layers, country, aggregationLevel },
  } = useQueryState();
  const {
    getCountryAdministrativeUnit: { isFetching: isLoadingGeoUnit },
  } = useCountries({ country, aggregationLevel });
  const {
    getClimateTimeseries: {
      data: climateTimeseriesData,
      isFetching: isLoadingClimate,
    },
  } = useClimateTimeseries({ isEnabled: type === "climate" });
  const {
    getHybridIndexTimeseries: {
      data: hybridTimeseries,
      isFetching: isLoadingHybridTimeseries,
    },
  } = useHybridIndexTimeseries({ isEnabled: type === "anomaly", season });
  const { data: anomalyTimeseriesData, isFetching: isLoadingAnomaly } =
    useTimeseriesAnomaly({ isEnabled: type === "anomaly" });
  const { data: droughtEventsData, isFetching: isLoadingDroughtEvent } =
    useDroughtEvent();
  const { data: ipcScoreData, isFetching: isLoadingIpcScore } = useIPCScore();

  const timeseriesData = useMemo(() => {
    if (type === "climate" && climateTimeseriesData)
      return [climateTimeseriesData?.data];
    else if (
      type === "anomaly" &&
      (anomalyTimeseriesData || hybridTimeseries)
    ) {
      const layerDisplayName = new Set(
        layers
          .filter((layer) => layer.season === season && layer.active)
          .map((layer) =>
            buildIndicatorName(layer.displayName, {
              isLandCoverMasked,
              isDetrended,
            }),
          ),
      );
      const resultAnomaly = anomalyTimeseriesData.filter(
        (ts) => layerDisplayName.has(ts.indicator) && ts.season === season,
      );

      if (
        hybridTimeseries?.data.season === season &&
        layerDisplayName.has(
          buildIndicatorName("Hybrid Index", { isLandCoverMasked }),
        )
      ) {
        resultAnomaly.push(hybridTimeseries.data);
      }
      return resultAnomaly;
    } else return [];
  }, [
    layers,
    season,
    hybridTimeseries,
    anomalyTimeseriesData,
    climateTimeseriesData,
    type,
    isDetrended,
    isLandCoverMasked,
  ]);

  const plot = usePlot({
    droughtEventData:
      droughtEvents && droughtEventsData?.data
        ? (droughtEventsData?.data?.data ?? [])
        : [],
    ipcScoreData:
      ipcScore && ipcScoreData?.data ? (ipcScoreData.data.data ?? []) : [],
    data: timeseriesData,
    type,
  });

  return {
    ...plot,
    isLoadingClimate:
      isLoadingClimate ||
      isLoadingIpcScore ||
      isLoadingDroughtEvent ||
      isLoadingGeoUnit,
    isLoadingAnomaly:
      isLoadingAnomaly ||
      isLoadingHybridTimeseries ||
      isLoadingIpcScore ||
      isLoadingDroughtEvent ||
      isLoadingGeoUnit,
  };
};

export default useTimeseriesSimple;
